import { type ReactNode, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import classes from "./FixedWidthTileCarousel.module.less";
import { IconType } from "../icon/IconType";
import { Icon } from "~/common/components/icon/Icon";
import { IconButton } from "@mui/material";
import { useIsMinDesktopSize } from "../../hooks/useMediaQuery";

export interface CarouselProps<T> {
  className?: string;
  items: T[];
  itemWidth: number; // px
  gap: number; // px
  children: (item: T) => ReactNode;
  hideButtons?: boolean;
}

export const FixedWidthTileCarousel = <T,>(props: CarouselProps<T>) => {
  const isDesktop = useIsMinDesktopSize();

  const { className, items, itemWidth, gap, children, hideButtons } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const tilesRef = useRef<(HTMLDivElement | null)[]>([]);

  const [scrollable, setScrollable] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pageItemsCount, setPageItemsCount] = useState(0);
  const [isPartiallyVisible, setIsPartiallyVisible] = useState(false);
  const [contentAreaWidth, setContentAreaWidth] = useState(0);

  useEffect(() => {
    const checkScrollable = () => {
      if (hideButtons) {
        return;
      }
      if (containerRef.current && items.length !== 0) {
        const { width: containerWidth } = containerRef.current.getBoundingClientRect();
        if (containerWidth === 0) {
          requestAnimationFrame(checkScrollable);
          return;
        }
        setContentAreaWidth(containerWidth);
        const totalWidth = items.length * itemWidth + (items.length - 1) * gap;
        setScrollable(totalWidth > containerWidth);

        const isPartiallyVisible = containerWidth % (itemWidth + gap) !== 0;
        setIsPartiallyVisible(isPartiallyVisible);

        const pageItemsCount = Math.ceil(containerWidth / (itemWidth + gap));
        setPageItemsCount(pageItemsCount);
      }
    };
    checkScrollable();
    window.addEventListener("resize", checkScrollable);

    return () => {
      window.removeEventListener("resize", checkScrollable);
    };
  }, [items.length, itemWidth, gap, hideButtons]);

  const scrollRight = () => {
    let nextIndex = currentIndex + pageItemsCount;
    nextIndex = isPartiallyVisible ? nextIndex - 1 : nextIndex;
    if (items.length <= nextIndex) {
      nextIndex = items.length - 1;
    }
    setCurrentIndex(nextIndex);
    scrollToIndex(nextIndex);
  };

  const scrollLeft = () => {
    let prevIndex = currentIndex - pageItemsCount;
    prevIndex = isPartiallyVisible ? prevIndex + 1 : prevIndex;
    if (prevIndex <= 0) {
      prevIndex = 0;
    }
    setCurrentIndex(prevIndex);
    scrollToIndex(prevIndex);
  };

  const scrollToIndex = (index: number) => {
    const element = tilesRef.current[index];
    if (element) {
      element.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
    }
  };

  const itemsRowWidth = pageItemsCount * itemWidth + (pageItemsCount - 1) * gap;

  const showButtons = scrollable && isDesktop && !hideButtons;

  return (
    <div className={classNames(classes.root, className)}>
      <div
        ref={containerRef}
        className={classNames(isDesktop ? classes.carouselContent : classes.sliderMobile)}
        style={{ gap: `${gap}px` }}
      >
        {items.map((item, index) => (
          <div
            key={index}
            ref={(el) => (tilesRef.current[index] = el)}
            style={index === items.length - 1 ? { paddingRight: contentAreaWidth - itemsRowWidth } : {}}
          >
            {children(item)}
          </div>
        ))}
      </div>

      {showButtons && (
        <>
          {currentIndex !== 0 && (
            <div className={classes.iconArrowPrev}>
              <IconButton
                size="large"
                variant="contained"
                color="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  scrollLeft();
                }}
              >
                <Icon type={IconType.ChevronLeft} />
              </IconButton>
            </div>
          )}
          {items.length - currentIndex >= pageItemsCount && (
            <div className={classes.iconArrowNext}>
              <IconButton
                size="large"
                variant="contained"
                color="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  scrollRight();
                }}
              >
                <Icon type={IconType.ChevronRight} />
              </IconButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};
